// Italian euro formatting: 7.19 -> "7,19"
export const euro = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

export const priceLabel = (p) => {
  if (p.price_to && p.price_to > p.price_from) {
    return `€${euro(p.price_from)} – €${euro(p.price_to)}`;
  }
  return `€${euro(p.price_from)}`;
};

export const fromLabel = (p) => `Da €${euro(p.price_from)}`;

// Extract Basic / Premium prices from a product's qualities.
// Colour/label follow the quality NAME so a Premium-only product renders the
// dark Premium pill (never the light Basic one).
export const qualityPrices = (p) => {
  const qs = p.qualities || [];
  const norm = (q) => (q.name || "").toLowerCase();
  let basic = qs.find((q) => norm(q).includes("basic")) || null;
  let premium = qs.find((q) => norm(q).includes("premium")) || null;
  if (!basic && !premium) {
    // Qualities not named basic/premium: first is basic, last is premium.
    basic = qs[0] || null;
    premium = qs.length > 1 ? qs[qs.length - 1] : null;
  } else if (basic && !premium && qs.length > 1) {
    premium = qs.find((q) => q !== basic) || null;
  }
  if (premium && basic && premium === basic) premium = null;
  return {
    basic: basic ? { name: basic.name, price: basic.price } : null,
    premium: premium ? { name: premium.name, price: premium.price } : null,
  };
};

// SQUIDDI-OPT: una sola palette per le qualità in tutto il sito cliente - 2026-06-17
// Classi definite in index.css: Basic lilla chiaro, Premium viola scuro.
export const qualityTone = (name = "") => (/premium/i.test(name) ? "q-tone-premium" : "q-tone-basic-sm");

// Resolve an image src: absolute URLs pass through; stored paths (/api/files/…)
// are prefixed with the backend origin so they render inside <img> tags.
// Con `width` il backend restituisce una miniatura WebP (`?w=400`): una card
// del catalogo pesa una frazione dell'originale.
const _BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const imgUrl = (src, width) => {
  if (!src) return "";
  let path = src;
  if (/^(https?:|data:|blob:)/.test(src)) {
    // Vecchi URL assoluti (ambienti di anteprima): il file è lo stesso, serviamo
    // il percorso dal backend corrente. Tutto il resto passa invariato.
    const m = src.match(/\/api\/(?:files|static)\/.+$/);
    if (!m) return src;
    path = m[0];
  }
  if (!path.startsWith("/api/")) return path;
  const resize = width && path.startsWith("/api/files/") ? `?w=${width}` : "";
  return `${_BACKEND}${path}${resize}`;
};

// srcset per schermi normali e retina: il browser scarica solo la misura che serve.
export const imgSrcSet = (src, widths = [400, 800]) => {
  if (!src) return undefined;
  const set = widths.map((w) => `${imgUrl(src, w)} ${w}w`);
  return set[0].includes("?w=") ? set.join(", ") : undefined;
};

// Elegant inline fallback shown if a remote product photo fails to load.
// Neutral pastel background + Squiddi wordmark — no fake garment
// silhouette (per user request: "cancella queste cose non farle piu").
const _fbSvg =
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 500' preserveAspectRatio='xMidYMid meet'>" +
  "<defs><linearGradient id='bg' x1='0' y1='0' x2='1' y2='1'>" +
  "<stop offset='0%' stop-color='#F5F3FB'/><stop offset='100%' stop-color='#EDE9F7'/>" +
  "</linearGradient></defs>" +
  "<rect width='100%' height='100%' fill='url(#bg)'/>" +
  "<text x='200' y='245' font-family='Inter, Arial, sans-serif' font-weight='800' font-size='22' letter-spacing='3' fill='#8C52FF' text-anchor='middle' opacity='0.7'>SQUIDDI</text>" +
  "<text x='200' y='272' font-family='Inter, Arial, sans-serif' font-weight='500' font-size='11' fill='#8C52FF' text-anchor='middle' opacity='0.4'>Foto non disponibile</text>" +
  "</svg>";
export const IMG_FALLBACK = `data:image/svg+xml,${encodeURIComponent(_fbSvg)}`;

// SQUIDDI-OPT: Consegna stimata - 2026-06-14
// Tempo di produzione del prodotto (giorni lavorativi, si prende il massimo
// della forbice) + 2 giorni lavorativi di spedizione. Sabato e domenica non
// contano. Le tariffe/tempi corriere verranno riaffinati a parte.
export const deliveryEstimate = (productionTime, from = new Date()) => {
  const raw = String(productionTime || "");
  const nums = (raw.match(/\d+/g) || []).map(Number);
  const top = nums.length ? Math.max(...nums) : 2;
  const prod = /ore|\bh\b/i.test(raw) ? Math.ceil(top / 24) : top;
  const day = new Date(from);
  let left = Math.max(1, prod) + 2;
  while (left > 0) {
    day.setDate(day.getDate() + 1);
    const wd = day.getDay();
    if (wd !== 0 && wd !== 6) left -= 1;
  }
  return day.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });
};
